import { FC, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Center,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  TextInput,
  Text,
  Anchor,
} from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import useAuthStore from "../../store/useAuthStore";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../utils/helpers";

interface Props {
  onSignUp: () => void;
  isSocialLoading: boolean;
}

export const SignUpForm: FC<Props> = ({ onSignUp, isSocialLoading }) => {
  const { t } = useTranslation();
  const { register } = useAuthStore();

  const [loading, setLoading] = useState(false);

  const matches = useMediaQuery("(max-width: 520px)");
  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirmPassword: "",
      terms: false,
    },
    validate: {
      first_name: isNotEmpty(t("first_name_required")),
      last_name: isNotEmpty(t("last_name_required")),
      email: isEmail(t("invalid_email_address")),
      password: (value: string) => {
        if (value.length < 8) return t("password_length_error");
        if (!/[0-9]/.test(value)) return t("password_number_error");
        if (!/[A-Z]/.test(value)) return t("password_uppercase_error");
        return null;
      },
      confirmPassword: (value, values) => {
        if (value !== values.password) return t("passwords_do_not_match");
        return null;
      },
      terms: (value: boolean) => (value ? null : t("terms_required")),
    },
  });

  const focusNextInput = (currentName: string) => {
    const fieldOrder = [
      "first_name",
      "last_name",
      "email",
      "password",
      "confirmPassword",
      "checkbox",
    ];
    const currentIndex = fieldOrder.indexOf(currentName);
    const nextField = fieldOrder[currentIndex + 1];
    if (nextField) {
      const next = document.querySelector(
        `input[name="${nextField}"]`
      ) as HTMLElement | null;
      next?.focus();
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    form.setErrors({});

    try {
      const successRegister = await register({
        email: values.email.toLowerCase(),
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
      });

      setLoading(false);

      if (successRegister) {
        onSignUp();
      }
    } catch (error: any) {
      setLoading(false);

      if (error?.response?.data?.errors?.password) {
        form.setErrors({
          password: capitalizeFirstLetter(
            error.response.data.errors.password.join(" ")
          ),
        });
      } else if (error?.response?.data?.errors?.email) {
        form.setErrors({
          email: capitalizeFirstLetter(
            error.response.data.errors.email.join(" ")
          ),
        });
      } else {
        form.setErrors({
          general: "Something went wrong, please try again...",
        });
      }
    }
  };

  const names = (
    <>
      <TextInput
        name="first_name"
        placeholder={t("first_name_placeholder")}
        label={t("first_name_label")}
        {...form.getInputProps("first_name")}
        onKeyDown={async (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const valid = form.validateField("first_name");
            if (valid.hasError) return;
            focusNextInput("first_name");
          }
        }}
      />

      <TextInput
        name="last_name"
        placeholder={t("last_name_placeholder")}
        label={t("last_name_label")}
        {...form.getInputProps("last_name")}
        onKeyDown={async (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const valid = form.validateField("last_name");
            if (valid.hasError) return;
            focusNextInput("last_name");
          }
        }}
      />
    </>
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack align="stretch" justify="center" gap="24px">
        {matches ? (
          names
        ) : (
          <Group justify="center" align="start" grow>
            {names}
          </Group>
        )}
        <TextInput
          name="email"
          placeholder={t("email_placeholder")}
          label={t("email_label")}
          {...form.getInputProps("email")}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const valid = form.validateField("email");
              if (valid.hasError) return;
              focusNextInput("email");
            }
          }}
        />
        <PasswordInput
          name="password"
          placeholder={t("password_placeholder")}
          label={t("password_label")}
          {...form.getInputProps("password")}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const valid = form.validateField("password");
              if (valid.hasError) return;
              focusNextInput("password");
            }
          }}
        />
        <PasswordInput
          name="confirmPassword"
          placeholder={t("confirm_password_placeholder")}
          label={t("confirm_password_label")}
          {...form.getInputProps("confirmPassword")}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const valid = form.validateField("confirmPassword");
              if (valid.hasError) return;
              focusNextInput("confirmPassword");
            }
          }}
        />

        <Center>
          <Checkbox
            name="checkbox"
            label={
              <Text>
                {t("agree_terms")}{" "}
                <Anchor
                  c="dark"
                  to="/terms-of-service"
                  underline="always"
                  component={Link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("terms_of_service")}
                </Anchor>{" "}
                {t("and")}{" "}
                <Anchor
                  c="dark"
                  to="/privacy-policy"
                  underline="always"
                  component={Link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("privacy_policy")}
                </Anchor>
              </Text>
            }
            {...form.getInputProps("terms", { type: "checkbox" })}
          />
        </Center>
        <Button fullWidth type="submit" loading={loading || isSocialLoading}>
          {t("sign_up_button")}
        </Button>
      </Stack>
    </form>
  );
};
